import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Paper,
  TextField,
  Typography,
  CircularProgress,
  Button,
  Fade,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';

type ChatMessage = {
  role: 'user' | 'assistant';
  content: string;
};

const ChatbotWidget: React.FC = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    const browserLang = (typeof navigator !== 'undefined' && (navigator.languages?.[0] || navigator.language)) || 'en';
    const primary = String(browserLang).toLowerCase().split('-')[0];
    const greetingByLang: Record<string, string> = {
      en: "Hi! I'm the Social In One assistant. How can I help?",
      tr: 'Merhaba! Ben Social In One asistanıyım. Nasıl yardımcı olabilirim?',
      nl: 'Hoi! Ik ben de Social In One assistent. Waarmee kan ik helpen?',
      de: 'Hallo! Ich bin der Social In One Assistent. Wobei kann ich helfen?',
      es: '¡Hola! Soy el asistente de Social In One. ¿En qué puedo ayudar?'
    };
    const content = greetingByLang[primary] || greetingByLang.en;
    return [{ role: 'assistant', content }];
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(true);
  const listRef = useRef<HTMLDivElement>(null);

  // Quick replies for /app routes
  const isInApp = location.pathname.startsWith('/app');
  const quickReplies = isInApp ? [
    "What is Prompt Tester and how does it work?",
    "I want to add a custom prompt",
    "How do I activate automatic responses?"
  ] : [];

  useEffect(() => {
    if (open && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [open, messages]);

  const toggle = () => setOpen(v => !v);

  const send = async (customText?: string) => {
    const text = customText || input.trim();
    if (!text || loading) return;
    if (!customText) setInput('');
    setShowQuickReplies(false); // Hide quick replies after first message
    const next = [...messages, { role: 'user', content: text } as ChatMessage];
    setMessages(next);
    setLoading(true);
    try {
      const baseUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8080';
      const browserLang = (typeof navigator !== 'undefined' && (navigator.languages?.[0] || navigator.language)) || 'en';
      const res = await fetch(`${baseUrl}/api/assistant/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: next, context: `browserLanguage: ${browserLang}; path: ${location.pathname}` })
      });
      const data = await res.json();
      const reply = (data && data.reply) || 'Şu anda yanıt veremiyorum.';
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Bir hata oluştu. Lütfen daha sonra tekrar deneyin.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickReply = (text: string) => {
    send(text);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Launcher Button */}
      <AnimatePresence>
        {!open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 10 }}
            transition={{ duration: 0.25 }}
            style={{ position: 'fixed', right: 24, bottom: 24, zIndex: 1400 }}
          >
            <IconButton
              color="primary"
              onClick={toggle}
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
                background: 'linear-gradient(135deg, #10B981, #059669)',
                color: 'white',
                '&:hover': { background: 'linear-gradient(135deg, #059669, #047857)' }
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'block',
                  width: 44,
                  height: 44,
                  bgcolor: 'white',
                  WebkitMask: 'url(/chatbot.svg) no-repeat center / contain',
                  mask: 'url(/chatbot.svg) no-repeat center / contain'
                }}
                aria-label="Chatbot"
              />
            </IconButton>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            style={{ position: 'fixed', right: 24, bottom: 24, zIndex: 1500, width: 360, maxWidth: 'calc(100vw - 32px)' }}
          >
            <Paper elevation={6} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)' }}>
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1.25,
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.1))',
                  borderBottom: '1px solid rgba(0,0,0,0.06)'
                }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Chat Assistant</Typography>
                  <Typography variant="caption" color="text.secondary">Online</Typography>
                </Box>
                <IconButton size="small" onClick={toggle}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Messages */}
              <Box ref={listRef} sx={{ maxHeight: 420, minHeight: 280, overflowY: 'auto', p: 2, backgroundColor: 'rgba(0,0,0,0.02)' }}>
                {messages.map((m, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.18 }}
                    style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 8 }}
                  >
                    <Box
                      sx={{
                        maxWidth: '85%',
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        backgroundColor: m.role === 'user' ? 'rgba(16, 185, 129, 0.12)' : 'white',
                        border: '1px solid rgba(0,0,0,0.06)',
                        color: 'text.primary',
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{m.content}</Typography>
                    </Box>
                  </motion.div>
                ))}

                {/* Quick Reply Suggestions */}
                {showQuickReplies && quickReplies.length > 0 && messages.length === 1 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                  >
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, fontWeight: 600 }}>
                        Quick Questions:
                      </Typography>
                      {quickReplies.map((reply, idx) => (
                        <Chip
                          key={idx}
                          label={reply}
                          onClick={() => handleQuickReply(reply)}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: 'white',
                            border: '1px solid rgba(16, 185, 129, 0.3)',
                            color: '#059669',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            justifyContent: 'flex-start',
                            '&:hover': {
                              backgroundColor: 'rgba(16, 185, 129, 0.08)',
                              borderColor: 'rgba(16, 185, 129, 0.5)',
                            },
                            '& .MuiChip-label': {
                              whiteSpace: 'normal',
                              textAlign: 'left',
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </motion.div>
                )}

                {loading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', mt: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption">Assistant typing…</Typography>
                  </Box>
                )}
              </Box>

              {/* Composer */}
              <Box sx={{ p: 1.25, display: 'flex', alignItems: 'center', gap: 1, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Bir mesaj yazın…"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <IconButton color="primary" onClick={() => send()} disabled={loading || !input.trim()}>
                  <SendIcon fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatbotWidget;


